// admin-panel/src/pages/Services.jsx
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { servicesApi } from '../lib/api';
import { Plus, Edit2, Trash2, Save, X, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = { title:'', slug:'', short_description:'', base_price:'', price_unit:'per project', sort_order:0, is_active:true, is_featured:false };

export default function Services() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // null | 'new' | service obj
  const [form, setForm] = useState(EMPTY);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const { data: services=[], isLoading } = useQuery({ queryKey:['admin-services'], queryFn: ()=>servicesApi.list({per_page:50}) });
  const list = services?.data || services;

  const saveMut = useMutation({
    mutationFn: (d) => modal?.id ? servicesApi.update(modal.id, d) : servicesApi.create(d),
    onSuccess: () => { qc.invalidateQueries(['admin-services']); setModal(null); toast.success('Service saved'); },
    onError: (e) => toast.error(e.message||'Save failed'),
  });

  const delMut = useMutation({
    mutationFn: servicesApi.delete,
    onSuccess: () => { qc.invalidateQueries(['admin-services']); toast.success('Deleted'); },
  });

  function openEdit(svc) {
    setForm({ title:svc.title||'', slug:svc.slug||'', short_description:svc.short_description||'', base_price:svc.base_price||'', price_unit:svc.price_unit||'per project', sort_order:svc.sort_order||0, is_active:svc.is_active??true, is_featured:svc.is_featured??false });
    setModal(svc);
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Services</h1>
        <button onClick={() => { setForm(EMPTY); setModal('new'); }} className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:opacity-90 transition-all">
          <Plus size={16}/> Add Service
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? Array(6).fill(0).map((_,i)=><div key={i} className="h-36 rounded-xl bg-white/5 animate-pulse"/>) :
        list.map(svc => (
          <div key={svc.id} className="bg-[#151515] border border-white/5 rounded-xl p-5 hover:border-[#D4AF37]/20 transition-all group">
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><Wrench size={18} className="text-[#D4AF37]"/></div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={()=>openEdit(svc)} className="p-1.5 rounded-lg hover:bg-white/10 text-[#888] hover:text-white transition-all"><Edit2 size={13}/></button>
                <button onClick={()=>{if(window.confirm('Delete?')) delMut.mutate(svc.id)}} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#888] hover:text-red-400 transition-all"><Trash2 size={13}/></button>
              </div>
            </div>
            <h3 className="text-white font-semibold mb-1">{svc.title}</h3>
            <p className="text-[#666] text-xs mb-3 line-clamp-2">{svc.short_description}</p>
            <div className="flex items-center justify-between">
              <span className="text-[#D4AF37] text-sm font-semibold">₹{Number(svc.base_price||0).toLocaleString('en-IN')}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${svc.is_active ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}>{svc.is_active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold">{modal?.id ? 'Edit' : 'New'} Service</h3>
              <button onClick={()=>setModal(null)}><X size={18} className="text-[#888] hover:text-white"/></button>
            </div>
            <div className="space-y-3">
              {[{k:'title',l:'Title *',p:'e.g. Modular Kitchen'},{k:'slug',l:'Slug',p:'modular-kitchen'},{k:'base_price',l:'Base Price (₹)',p:'85000',t:'number'},{k:'price_unit',l:'Price Unit',p:'per project'}].map(({k,l,p,t})=>(
                <div key={k}>
                  <label className="block text-xs text-[#555] mb-1.5">{l}</label>
                  <input type={t||'text'} value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={p}
                    className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30"/>
                </div>
              ))}
              <div>
                <label className="block text-xs text-[#555] mb-1.5">Short Description</label>
                <textarea value={form.short_description} onChange={e=>set('short_description',e.target.value)} rows={3} placeholder="Brief description…"
                  className="w-full bg-[#121212] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none"/>
              </div>
              <div className="flex gap-4">
                {[{k:'is_active',l:'Active'},{k:'is_featured',l:'Featured'}].map(({k,l})=>(
                  <label key={k} className="flex items-center gap-2 cursor-pointer">
                    <div onClick={()=>set(k,!form[k])} className={`w-9 h-5 rounded-full relative transition-all ${form[k]?'bg-[#D4AF37]':'bg-white/10'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${form[k]?'left-4':'left-0.5'}`}/>
                    </div>
                    <span className="text-sm text-[#888]">{l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={()=>setModal(null)} className="flex-1 py-2.5 rounded-xl text-sm text-[#888] bg-white/5 hover:bg-white/10 transition-all">Cancel</button>
              <button onClick={()=>saveMut.mutate(form)} disabled={saveMut.isPending} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
                <Save size={14}/>{saveMut.isPending?'Saving…':'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
